import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '@moko/ui';
import { FontIcon, IconButton, Link } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useSetAtom } from 'jotai';
import { fileToBase64, formatDate } from '@moko/utils';
import {
  successMessageAtom,
  errorMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  getReportTemplates,
  uploadReportTemplate,
  updateReportTemplate,
  downloadReportTemplate,
  deleteReportTemplate,
  type IReportTemplate,
} from '../../services/reportTemplateService';
import ConfirmDialog from '../../common/ConfirmDialog';
import AppContext from '../AppContext';
import DownloadReportTemplatePanel from './DownloadReportTemplatePanel';

const ACCEPTED_FILE_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const PAGE_SIZE = 10;
const TITLE = 'Report template';

const ReportTemplatesList = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);

  const gridRef = useRef<IPagedDataGridRefType>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceTargetRef = useRef<IReportTemplate | null>(null);

  const [pendingDelete, setPendingDelete] = useState<IReportTemplate | null>(
    null
  );
  const [
    isDeleteConfirmOpen,
    { setTrue: showDeleteConfirm, setFalse: hideDeleteConfirm },
  ] = useBoolean(false);
  const [
    isDownloadTemplatePanelOpen,
    { setTrue: showDownloadTemplatePanel, setFalse: hideDownloadTemplatePanel },
  ] = useBoolean(false);

  useEffect(() => {
    context.setSelectedTab(TITLE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshGrid = useCallback(() => gridRef.current?.refresh(), []);

  const handleDownload = useCallback(
    async (template: IReportTemplate) => {
      try {
        const abortController = new AbortController();
        const response = await downloadReportTemplate(
          abortController,
          template.id
        );
        const blob = await response.blob();

        let fileName = template.fileName;
        const disposition = response.headers.get('Content-Disposition');
        if (disposition) {
          const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/);
          if (match?.[1]) fileName = match[1];
        }

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      } catch (error: any) {
        setErrorMessage(error?.message || 'Failed to download template.');
      }
    },
    [setErrorMessage]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.type !== ACCEPTED_FILE_TYPE && !file.name.endsWith('.docx')) {
        setErrorMessage('Only .docx files are allowed.');
        event.target.value = '';
        return;
      }

      try {
        const base64 = await fileToBase64(file);
        const abortController = new AbortController();
        const result = await uploadReportTemplate(abortController, {
          content: base64,
          name: file.name,
          fileName: file.name,
          contentType: ACCEPTED_FILE_TYPE,
          entityType: 'Work',
        });
        setSuccessMessage(
          `Template "${file.name}" uploaded successfully (ID: ${result.reportTemplateId}).`
        );
        refreshGrid();
      } catch (error: any) {
        setErrorMessage(error?.message || 'Failed to upload template.');
      } finally {
        event.target.value = '';
      }
    },
    [setSuccessMessage, setErrorMessage, refreshGrid]
  );

  const requestReplaceFile = useCallback((template: IReportTemplate) => {
    replaceTargetRef.current = template;
    replaceInputRef.current?.click();
  }, []);

  const handleReplaceFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const target = replaceTargetRef.current;
      if (!file || !target) {
        event.target.value = '';
        return;
      }

      if (file.type !== ACCEPTED_FILE_TYPE && !file.name.endsWith('.docx')) {
        setErrorMessage('Only .docx files are allowed.');
        event.target.value = '';
        return;
      }

      try {
        const base64 = await fileToBase64(file);
        const abortController = new AbortController();
        await updateReportTemplate(abortController, target.id, {
          name: target.name || target.fileName,
          content: base64,
          fileName: file.name,
          contentType: ACCEPTED_FILE_TYPE,
          entityType: target.entityType || 'Work',
        });
        setSuccessMessage(`Template file replaced for "${target.fileName}".`);
        refreshGrid();
      } catch (error: any) {
        setErrorMessage(error?.message || 'Failed to replace template file.');
      } finally {
        replaceTargetRef.current = null;
        event.target.value = '';
      }
    },
    [setSuccessMessage, setErrorMessage, refreshGrid]
  );

  const requestDelete = useCallback(
    (template: IReportTemplate) => {
      setPendingDelete(template);
      showDeleteConfirm();
    },
    [showDeleteConfirm]
  );

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    const abortController = new AbortController();
    deleteReportTemplate(abortController, pendingDelete.id)
      .then(() => {
        setSuccessMessage(
          `Template "${pendingDelete.fileName}" deleted successfully.`
        );
        setPendingDelete(null);
        refreshGrid();
      })
      .catch((error: any) => {
        setErrorMessage(error?.message || 'Failed to delete template.');
      });
    hideDeleteConfirm();
  }, [
    pendingDelete,
    setSuccessMessage,
    setErrorMessage,
    hideDeleteConfirm,
    refreshGrid,
  ]);

  const columns: DataGridColumn<IReportTemplate>[] = useMemo(
    () => [
      {
        key: 'name',
        name: 'Report name',
        minWidth: 220,
        ellipsis: true,
        isSortable: true,
        renderCell: (item) => (
          <Link
            onClick={() =>
              navigate('/editReportTemplate', { state: { template: item } })
            }
          >
            {item.name?.trim() ? item.name : 'N/A'}
          </Link>
        ),
      },
      {
        key: 'fileName',
        name: 'File name',
        minWidth: 200,
        ellipsis: true,
        isSortable: true,
        renderCell: (item) => (
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={item.fileName}
          >
            {item.fileName}
          </span>
        ),
      },
      {
        key: 'createdBy',
        name: 'Uploaded by',
        width: 160,
        isSortable: true,
      },
      {
        key: 'createdAt',
        name: 'Uploaded at',
        width: 180,
        isSortable: true,
        renderCell: (item) => (
          <span>{item.createdAt ? formatDate(item.createdAt, true) : ''}</span>
        ),
      },
      {
        key: 'actions',
        name: '',
        width: 48,
        renderCell: (item) => (
          <IconButton
            menuIconProps={{ iconName: 'More' }}
            title="Actions"
            ariaLabel="Actions"
            styles={{ menuIcon: { fontSize: 18 } }}
            menuProps={{
              items: [
                {
                  key: 'edit',
                  text: 'Edit',
                  iconProps: { iconName: 'Edit' },
                  onClick: () => {
                    navigate('/editReportTemplate', {
                      state: { template: item },
                    });
                  },
                },
                {
                  key: 'download',
                  text: 'Download',
                  iconProps: { iconName: 'Download' },
                  onClick: () => {
                    handleDownload(item);
                  },
                },
                {
                  key: 'upload',
                  text: 'Upload',
                  iconProps: { iconName: 'Upload' },
                  // Replace the file in place via the Update API.
                  onClick: () => {
                    requestReplaceFile(item);
                  },
                },
                {
                  key: 'duplicate',
                  text: 'Duplicate',
                  iconProps: { iconName: 'Copy' },
                  onClick: () => {
                    navigate('/duplicateReportTemplate', {
                      state: { template: item },
                    });
                  },
                },
                {
                  key: 'delete',
                  text: 'Delete',
                  iconProps: { iconName: 'Delete' },
                  onClick: () => {
                    requestDelete(item);
                  },
                },
              ],
            }}
          />
        ),
      },
    ],
    [navigate, handleDownload, requestReplaceFile, requestDelete]
  );

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: refreshGrid,
      },
      {
        key: 'new',
        text: 'New report',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/newReportTemplate'),
      },
      {
        key: 'downloadReportTemplate',
        text: 'Download report template',
        icon: <FontIcon iconName="WordDocument" />,
        onClick: showDownloadTemplatePanel,
      },
    ],
    [navigate, refreshGrid, handleUploadClick, showDownloadTemplatePanel]
  );

  return (
    <div style={{ maxWidth: 1200 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".docx"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept=".docx"
        style={{ display: 'none' }}
        onChange={handleReplaceFileSelected}
      />
      <PagedDataGrid<IReportTemplate>
        ref={gridRef}
        columns={columns}
        commandBarItems={commandBarItems}
        pageSize={PAGE_SIZE}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        ) => {
          return getReportTemplates(
            abortController,
            pageSize,
            pageNo,
            orderBy,
            search,
            filters
          );
        }}
        listHeigh={525}
      />
      <ConfirmDialog
        isModalOpen={isDeleteConfirmOpen}
        hideModal={hideDeleteConfirm}
        message={`Template "${pendingDelete?.fileName}" will be deleted. Are you sure?`}
        onYesClick={confirmDelete}
      />
      <DownloadReportTemplatePanel
        isOpen={isDownloadTemplatePanelOpen}
        onDismiss={hideDownloadTemplatePanel}
      />
    </div>
  );
};

export default ReportTemplatesList;
